import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Modal } from "../Modal";
import {
  MODAL_CARD_SHELL_STYLE,
  ModalContent,
  ModalFooter,
  ModalHead,
  ModalRow,
  ModalRows,
  ModalRowText,
  STEPPER_INPUT_STYLE,
} from "../ModalCard";
import { Stepper, StepButton } from "../Stepper";
import { Text } from "../Text";
import { AI_USAGE_ACTIVITY_LABELS } from "./aiUsageActivities";
import { useMonitoringAnalytics } from "./analytics";

const LabelGroup = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SourceBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 450;
  line-height: 14px;
  white-space: nowrap;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

const STEP_MINUTES = 5;

function adjustMinutes(current: string, delta: number) {
  const next = (parseNullableMinutes(current) ?? 0) + delta;
  return String(Math.max(0, next));
}

type ValueWorkflow = {
  id: string;
  name: string;
  spaceId: string;
  estimatedBuildMinutesSaved: number | null;
  estimatedMinutesSaved: number | null;
  timeSavedBuildSource: "llm" | "manual" | null;
  timeSavedSource: "llm" | "manual" | null;
};

type Props = { id: string; onClose: () => void };

function sourceLabel(source: "llm" | "manual" | null) {
  if (source === "llm") return "AI estimate";
  if (source === "manual") return "Edited";
  return "Not set";
}

function parseNullableMinutes(value: string) {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
}

export function WorkflowValueModal({ id, onClose }: Props) {
  const [workflows] = useQuery(queries.monitoringWorkflowValue());
  const workflow = workflows.find((w) => w.id === id);
  const canWrite = useHasAccess("space:write", workflow?.spaceId);

  if (!workflow) return null;

  return (
    <Modal open onClose={onClose} style={MODAL_CARD_SHELL_STYLE}>
      <ValueEditor
        workflow={workflow as ValueWorkflow}
        canWrite={canWrite}
        onClose={onClose}
      />
    </Modal>
  );
}

function ValueEditor({
  workflow,
  canWrite,
  onClose,
}: {
  workflow: ValueWorkflow;
  canWrite: boolean;
  onClose: () => void;
}) {
  const zero = useZero();
  const monitoringAnalytics = useMonitoringAnalytics();
  const [draft, setDraft] = useState({
    build:
      workflow.estimatedBuildMinutesSaved == null
        ? ""
        : String(workflow.estimatedBuildMinutesSaved),
    run:
      workflow.estimatedMinutesSaved == null
        ? ""
        : String(workflow.estimatedMinutesSaved),
  });

  const rows = [
    {
      key: "build" as const,
      label: AI_USAGE_ACTIVITY_LABELS.build,
      description:
        "Minutes saved building in 3B rather than manually coding. One-time calculation.",
      source: workflow.timeSavedBuildSource,
    },
    {
      key: "run" as const,
      label: AI_USAGE_ACTIVITY_LABELS.run,
      description: "Minutes saved per successful run",
      source: workflow.timeSavedSource,
    },
  ];

  async function save() {
    const patch: Parameters<typeof sharedMutators.setWorkflowTimeSaved>[0] = {
      analytics: monitoringAnalytics(),
      workflowId: workflow.id,
    };
    const newBuild = parseNullableMinutes(draft.build);
    if (newBuild !== workflow.estimatedBuildMinutesSaved) {
      patch.estimatedBuildMinutesSaved = newBuild;
    }
    const newRun = parseNullableMinutes(draft.run);
    if (newRun !== workflow.estimatedMinutesSaved) {
      patch.estimatedMinutesSaved = newRun;
    }
    zero.mutate(sharedMutators.setWorkflowTimeSaved(patch));
    onClose();
  }

  return (
    <ModalContent>
      <ModalHead>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Time saved
        </Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          These estimates drive the workflow’s savings.
        </Text>
      </ModalHead>
      <ModalRows>
        {rows.map((row) => (
          <ModalRow key={row.key}>
            <ModalRowText>
              <LabelGroup>
                <Text variant="text-regular-md" color="var(--text-primary)">
                  {row.label}
                </Text>
                <SourceBadge>{sourceLabel(row.source)}</SourceBadge>
              </LabelGroup>
              <Text variant="text-small" color="var(--text-secondary)">
                {row.description}
              </Text>
            </ModalRowText>
            {canWrite ? (
              <Stepper>
                <StepButton
                  data-no-pill
                  aria-label={`Decrease ${row.label}`}
                  disabled={(parseNullableMinutes(draft[row.key]) ?? 0) <= 0}
                  onPress={() =>
                    setDraft((current) => ({
                      ...current,
                      [row.key]: adjustMinutes(current[row.key], -STEP_MINUTES),
                    }))
                  }
                >
                  <Icon name="minus" size={16} color="var(--text-secondary)" />
                </StepButton>
                <Input
                  value={draft[row.key]}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      [row.key]: event.target.value,
                    }))
                  }
                  placeholder="0"
                  inputMode="numeric"
                  style={STEPPER_INPUT_STYLE}
                />
                <StepButton
                  data-no-pill
                  aria-label={`Increase ${row.label}`}
                  onPress={() =>
                    setDraft((current) => ({
                      ...current,
                      [row.key]: adjustMinutes(current[row.key], STEP_MINUTES),
                    }))
                  }
                >
                  <Icon name="plus" size={16} color="var(--text-secondary)" />
                </StepButton>
              </Stepper>
            ) : (
              <Text variant="text-small">
                {draft[row.key] === "" ? "—" : `${draft[row.key]} min`}
              </Text>
            )}
          </ModalRow>
        ))}
      </ModalRows>
      <ModalFooter>
        <Button size="l" variant="subtle" onClick={onClose}>
          {canWrite ? "Cancel" : "Close"}
        </Button>
        {canWrite && (
          <Button size="l" variant="primary" onClick={save}>
            Save
          </Button>
        )}
      </ModalFooter>
    </ModalContent>
  );
}
